import { Box, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { now, vyDate, withDefault } from '@vy/utils';
import type { MutableRefObject } from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { formatDate } from '../../../utils/date';

import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import type { FilterLineType } from '../../../components/new-design/vyTableFilter';
import { useJournalEntriesReport } from '../../../hooks/requests/use-journal-entries-report';
import { ReportTable } from '../reports-components/ReportTable';
import { REPORT_TYPES } from '../reports-page/reports-page-types/report-type';
import { JournalEntriesReportTable } from './journal-entries-report-components/JournalEntriesReportTable';
import { JournalEntriesReportToolbar } from './journal-entries-report-components/JournalEntriesReportToolbar';
import { applyJournalEntriesFilters } from './utils/apply-journal-entries-filters';
import { flattenJournalEntries } from './utils/flatten-journal-entries';

const JournalEntriesReportContent: React.FC = () => {
  const { data: journalEntriesReport } = useJournalEntriesReport();

  const defaultMonth = useMemo(() => vyDate().startOf('month').toDate(), []);

  const [month, setMonth] = useState<Date | null>(defaultMonth);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filters, setFilters] = useState<FilterLineType[]>([]);

  const allRows = useMemo(
    () => flattenJournalEntries(journalEntriesReport.entries),
    [journalEntriesReport.entries],
  );

  const filteredRows = useMemo(
    () => applyJournalEntriesFilters(allRows, {
      month,
      customerSearch,
      filters,
    }),
    [allRows, month, customerSearch, filters],
  );

  const apiRef = useGridApiRef();

  const handleExportCsv = () => {
    const api = apiRef.current;
    if (!api) return;

    api.exportDataAsCsv({
      fileName: `Journal Entries Report (issued ${formatDate(now(), 'MMM D, YYYY')})`,
    });
  };

  const creationDate = withDefault(journalEntriesReport.reportGeneratedAt, new Date());

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "20px"
          }}>
          Journal Entries
        </Typography>
      </Box>
      <ReportTable
        title="Journal Entries"
        hideTitle
        exportCsvFileName="Journal Entries Report"
        reportCreationDate={creationDate ?? new Date()}
        reportType={REPORT_TYPES.JOURNAL_ENTRIES}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        hideMenu
      >
        <JournalEntriesReportToolbar
          month={month}
          onMonthChange={setMonth}
          customerSearch={customerSearch}
          onCustomerSearchChange={setCustomerSearch}
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExportCsv}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <JournalEntriesReportTable rows={filteredRows} apiRef={apiRef as MutableRefObject<GridApiPremium>} />
        </Box>
      </ReportTable>
    </>
  );
};

const JournalEntriesReportPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Journal Entries Report</title>
      </Helmet>

      <VyLayout>
        <JournalEntriesReportContent />
      </VyLayout>
    </>
  );
};

const JournalEntriesReportPageWithSuspenseBoundary = withSuspenseBoundary(JournalEntriesReportPage);

export { JournalEntriesReportPageWithSuspenseBoundary as JournalEntriesReportPage };
